import React, { useState } from "react";
import { Button, Textarea, Modal } from "flowbite-react";
import { toast } from "../../../../toast";
import { gql, useMutation } from "@apollo/client";

/* ✅ RESEND INVITE MUTATION */
const RESEND_INVITE_MUTATION = gql`
  mutation ResendStudentInvite($email: String!) {
    resendStudentInvite(email: $email)
  }
`;

/* ✅ DELETE BULK STUDENTS MUTATION (RETURNS STRING) */
const DELETE_BULK_STUDENTS_MUTATION = gql`
  mutation DeleteBulkStudents($studentIds: [String!]!, $campusName: String!) {
    deleteBulkStudents(studentIds: $studentIds, campusName: $campusName) {
      successCount
      failedCount
      deletedEmails
      failedStudents {
        email
        studentId
        reason
      }
      message
    }
  }
`;

export const ACTION_MODES = {
  create: "create",
  resend: "resend",
  delete: "delete",
};

const BulkCreateStudentsStep = ({
  formData,
  setFormData,
  nextStep,
  actionMode = ACTION_MODES.create,
  campusName,
}) => {
  const [inputValue, setInputValue] = useState(formData.StudentsInput || "");
  const [inviteEmails, setInviteEmails] = useState("");
  const [deleteEmails, setDeleteEmails] = useState("");

  /* ✅ DELETE RESULT (STRUCTURED RESPONSE FROM BACKEND) */
  const [deleteResult, setDeleteResult] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [resendInvite] = useMutation(RESEND_INVITE_MUTATION);
  const [deleteBulkStudents] = useMutation(DELETE_BULK_STUDENTS_MUTATION);

  /* ✅ EMAIL HELPERS */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Comma, whitespace, newline, or semicolon separated (invite / delete textareas). */
  const parseEmails = (value = "") =>
    String(value || "")
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

  /** Delete textarea: treat each comma/newline/semicolon chunk as one email entry. */
  const parseDeleteEmails = (value = "") =>
    String(value || "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

  /**
   * Bulk student creation (first step) only: normalize BOM/CRLF, then split on
   * whitespace (spaces, tabs, newlines), commas, or semicolons.
   */
  const parseBulkCreateStudentEmails = (value = "") => {
    const normalized = String(value || "")
      .replace(/^\uFEFF/, "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n")
      .trim();
    if (!normalized) return [];
    return normalized
      .split(/[\s,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);
  };

  const validateEmails = (emails) =>
    Array.isArray(emails) &&
    emails.length > 0 &&
    emails.every((e) => emailRegex.test(e));

  const getInvalidEmails = (emails = []) =>
    (Array.isArray(emails) ? emails : []).filter((e) => !emailRegex.test(e));

  /* ✅ INPUT HANDLERS */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setFormData((prev) => ({ ...prev, StudentsInput: value }));
  };

  const handleInviteChange = (e) => setInviteEmails(e.target.value);
  const handleDeleteChange = (e) => setDeleteEmails(e.target.value);

  /* ✅ NEXT STEP */
  const handleNextStep = () => {
    const emails = parseBulkCreateStudentEmails(inputValue);

    if (!validateEmails(emails)) {
      toast.error("Invalid or empty email(s).");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      students: emails.map((email) => ({ email })),
    }));

    nextStep();
  };

  /* ✅ SEND INVITES */
  const handleSendInvites = async () => {
    const emails = parseEmails(inviteEmails);

    if (!validateEmails(emails)) {
      toast.error("Invalid invite email(s).");
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const email of emails) {
      try {
        await resendInvite({ variables: { email } });
        successCount++;
      } catch {
        failCount++;
      }
    }

    if (failCount === 0) {
      toast.success(`All ${successCount} invites sent successfully!`);
    } else {
      toast.warn(`${successCount} sent, ${failCount} failed.`);
    }

    setInviteEmails("");
  };

  /* ✅ DELETE BULK (SAFE WITH STRING RESPONSE) */
  const handleDeleteBulk = async () => {
    const emails = parseDeleteEmails(deleteEmails);

    const invalidEmails = getInvalidEmails(emails);
    if (emails.length === 0 || invalidEmails.length > 0) {
      if (invalidEmails.length > 0) {
        toast.error(`Invalid delete email(s): ${invalidEmails.join(", ")}`);
      } else {
        toast.error("Invalid delete email(s).");
      }
      return;
    }

    try {
      const res = await deleteBulkStudents({
        variables: {
          studentIds: emails,
          campusName: campusName || "KCG",
        },
      });

      const payload = res?.data?.deleteBulkStudents;
      const normalizedResult =
        payload && typeof payload === "object"
          ? payload
          : {
              successCount: emails.length,
              failedCount: 0,
              deletedEmails: emails,
              failedStudents: [],
              message: String(payload || "Delete operation completed."),
            };

      setDeleteResult(normalizedResult);
      setOpenDeleteModal(true);
      setDeleteEmails("");

      toast.success(normalizedResult.message || "Delete operation completed.");
    } catch (error) {
      console.error(error);
      toast.error("Bulk delete failed.");
    }
  };

  const textareaClass =
    "w-full flex-1 min-h-[160px] resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:border-[#0E3D5D] focus:ring-2 focus:ring-[#0E3D5D]/20";

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full h-full">
      <form className="flex flex-col flex-1 min-h-0 gap-4 w-full pt-0.5">
        {actionMode === ACTION_MODES.create && (
          <div className="flex flex-col flex-1 min-h-0 gap-3" role="region" aria-label="Bulk create students">
            <Textarea
              required
              value={inputValue}
              onChange={handleInputChange}
              placeholder="Enter student emails for bulk creation ( space-separated, one per line, or comma / semicolon)"
              className={textareaClass}
              rows={6}
            />
            <div className="flex justify-end shrink-0">
              <Button
                size="sm"
                className="bg-primary px-3 font-semibold shrink-0"
                onClick={handleNextStep}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {actionMode === ACTION_MODES.resend && (
          <div className="flex flex-col flex-1 min-h-0 gap-3" role="region" aria-label="Resend invite">
            <Textarea
              value={inviteEmails}
              onChange={handleInviteChange}
              placeholder="Enter emails to send invite ( comma, space, or one per line)"
              className={textareaClass}
              rows={6}
            />
            <div className="flex justify-end shrink-0">
              <Button
                size="sm"
                className="bg-primary px-3 font-semibold shrink-0"
                onClick={handleSendInvites}
              >
                Send Invites
              </Button>
            </div>
          </div>
        )}

        {actionMode === ACTION_MODES.delete && (
          <div className="flex flex-col flex-1 min-h-0 gap-3" role="region" aria-label="Bulk delete">
            <Textarea
              value={deleteEmails}
              onChange={handleDeleteChange}
              placeholder="Emails to delete - comma or one per line"
              className={textareaClass}
              rows={6}
            />
            <div className="flex justify-end shrink-0">
              <Button
                size="sm"
                className="bg-primary px-3 font-semibold shrink-0"
                onClick={handleDeleteBulk}
              >
                Delete Bulk Students
              </Button>
            </div>
          </div>
        )}
      </form>

      {/* ✅ DELETE RESULT MODAL (STRING DISPLAY) */}
      <Modal show={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <Modal.Header>Delete Summary</Modal.Header>
        <Modal.Body>
          {deleteResult ? (
            <div className="space-y-4 text-sm text-gray-700">
              <p className="whitespace-pre-line">
                {deleteResult.message || "Delete operation completed."}
              </p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                  <div className="text-xs font-semibold uppercase text-green-700">Deleted</div>
                  <div className="mt-1 text-2xl font-bold text-green-800">
                    {deleteResult.successCount ?? 0}
                  </div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="text-xs font-semibold uppercase text-red-700">Failed</div>
                  <div className="mt-1 text-2xl font-bold text-red-800">
                    {deleteResult.failedCount ?? 0}
                  </div>
                </div>
              </div>

              {(deleteResult.failedStudents?.length > 0) && (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Failed Emails
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {deleteResult.failedStudents.map((student, index) => (
                      <span
                        key={`${student.email || student.studentId || index}`}
                        className="rounded-full bg-red-100 px-2.5 py-1 text-xs text-red-800"
                      >
                        {student.email || "-"}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-line">No response from server.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setOpenDeleteModal(false);
              setDeleteResult(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BulkCreateStudentsStep;
